import { PaymentElement, useCheckout } from '@stripe/react-stripe-js'
import { AuthContext } from '@/contexts/AuthContext'
import axios from '@/lib/api/axios'
import { useContext, useState, useEffect } from 'react'
import { Button } from '@/components/ui/shadcn/button'
import { Input } from '@/components/ui/shadcn/input'
import { Label } from '@/components/ui/shadcn/label'
import { Card, CardContent } from '@/components/ui/shadcn/card'
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@/components/ui/shadcn/table'
import { Alert, AlertDescription } from '@/components/ui/shadcn/alert'
import { AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

export default function BillingPayment({
  selectedTier,
  teamSize,
  setTeamSize,
  onTierUpdate,
  handleNext,
  handleClose,
  handleBack,
  setLoading,
  sessionId,
  tiers,
  couponCode = '',
  setCouponCode = () => {},
  onCouponApply = () => {},
  initialCouponDiscount = null,
}) {
  const authContext = useContext(AuthContext)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [existingBilling, setExistingBilling] = useState(null)
  const [currentTierPrice, setCurrentTierPrice] = useState(0)
  const [couponDiscount, setCouponDiscount] = useState(initialCouponDiscount)
  const [couponError, setCouponError] = useState(null)
  const [validatingCoupon, setValidatingCoupon] = useState(false)
  const [applyingCoupon, setApplyingCoupon] = useState(false)
  const [appliedCouponCode, setAppliedCouponCode] = useState('')

  const checkout = useCheckout()

  useEffect(() => {
    if (initialCouponDiscount) {
      setCouponDiscount(initialCouponDiscount)
      setAppliedCouponCode(couponCode)
    }
  }, [initialCouponDiscount, couponCode])

  useEffect(() => {
    if (existingBilling?.tier) {
      const currentTier = tiers.find(t => t.name === existingBilling.tier)
      if (currentTier) {
        const currentTeamSize = existingBilling.teamSize || 1
        setCurrentTierPrice(currentTier.price * currentTeamSize)
      }
    }
  }, [existingBilling, tiers])

  const validateCoupon = async code => {
    if (!code.trim()) {
      setCouponDiscount(null)
      setCouponError(null)
      return null
    }

    setValidatingCoupon(true)
    setCouponError(null)

    try {
      const { data } = await axios(
        `/organization/${authContext.user.organization.id}/validate-coupon`,
        {
          method: 'POST',
          data: {
            couponCode: code.trim(),
            priceId: selectedTier.priceId,
            amount: calculateTotalCost() * 100,
          },
        },
        authContext,
        true,
        'billing',
      )
      return data
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Invalid coupon code'
      setCouponError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setValidatingCoupon(false)
    }
  }

  const handleCouponChange = e => {
    const value = e.target.value
    setCouponCode(value)
    if (value !== appliedCouponCode) {
      setCouponDiscount(null)
      setCouponError(null)
    }
  }

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code')
      return
    }

    if (couponCode.trim() === appliedCouponCode) {
      return
    }

    setApplyingCoupon(true)
    setError(null)

    try {
      const discount = await validateCoupon(couponCode.trim())
      setCouponDiscount(discount)
      await onCouponApply(couponCode.trim(), discount)
      setAppliedCouponCode(couponCode.trim())
    } catch (err) {
      console.error('Failed to apply coupon:', err)
      setCouponError(err.message || 'Failed to apply coupon')
      setCouponDiscount(null)
    } finally {
      setApplyingCoupon(false)
    }
  }

  const handleRemoveCoupon = async () => {
    setApplyingCoupon(true)
    setCouponCode('')
    setCouponDiscount(null)
    setCouponError(null)
    setAppliedCouponCode('')

    try {
      await onCouponApply('', null)
    } catch (err) {
      console.error('Failed to remove coupon:', err)
      setError('Failed to remove coupon')
    } finally {
      setApplyingCoupon(false)
    }
  }

  const handlePayment = async e => {
    e.preventDefault()
    setSubmitting(true)

    try {
      if (existingBilling?.hasActiveSubscription) {
        await axios(
          `/organization/${authContext.user.organization.id}/update-subscription`,
          {
            method: 'POST',
            data: {
              newPriceId: selectedTier.priceId,
              teamSize: teamSize,
              tier: selectedTier.name,
              couponCode: appliedCouponCode || undefined,
            },
          },
          authContext,
          true,
          'billing',
        )
        onTierUpdate(selectedTier.name)
        handleNext()
      } else {
        const { error } = await checkout.confirm({
          redirect: 'if_required',
        })

        if (error) {
          console.error('Failed to confirm payment:', error)
          setError(error)
          setSubmitting(false)
          return
        }

        await axios(
          `/organization/${authContext.user.organization.id}/tier`,
          {
            method: 'POST',
            data: {
              tier: selectedTier.name,
              sessionId: sessionId,
              priceId: selectedTier.priceId,
              teamSize: teamSize,
              couponCode: appliedCouponCode || undefined,
            },
          },
          authContext,
          true,
          'billing',
        )
        onTierUpdate(selectedTier.name)
        handleNext()
      }
    } catch (err) {
      console.error('Failed to update tier:', err)
      setError(err.response?.data?.message || 'Failed to update subscription')
    } finally {
      setSubmitting(false)
    }
  }

  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const { data: billing } = await axios(
          `/organization/${authContext.user.organization.id}/active-subscription`,
          { method: 'GET' },
          authContext,
          true,
          'billing',
        )
        setExistingBilling(billing)
        setLoading(false)
      } catch (err) {
        console.error('Failed to check subscription status:', err)
        setLoading(false)
      }
    }
    checkSubscription()
  }, [authContext, setLoading])

  const calculateTotalCost = () => {
    const basePrice = selectedTier.price
    const teamMultiplier = selectedTier.team
      ? teamSize || selectedTier.minTeamSize
      : 1
    return basePrice * teamMultiplier
  }

  const calculateDiscountedPrice = () => {
    const totalCost = calculateTotalCost()
    if (!couponDiscount) return totalCost

    if (couponDiscount.amountOff) {
      return Math.max(0, totalCost - couponDiscount.amountOff / 100)
    }

    if (couponDiscount.percentOff) {
      return totalCost * (1 - couponDiscount.percentOff / 100)
    }

    return totalCost
  }

  const calculatePriceDifference = () => {
    if (!existingBilling?.hasActiveSubscription) return 0
    return calculateDiscountedPrice() - currentTierPrice
  }

  const canApplyCoupon =
    couponCode.trim() && couponCode.trim() !== appliedCouponCode
  const hasAppliedCoupon = appliedCouponCode && couponDiscount

  const teamSizeInvalid = teamSize !== '' && teamSize < selectedTier.minTeamSize

  return (
    <form onSubmit={handlePayment} className="space-y-4">
      <input
        type="hidden"
        name="email"
        id="email"
        value={authContext.user.email}
      />

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertDescription>{error.message || String(error)}</AlertDescription>
        </Alert>
      )}

      {!existingBilling?.hasActiveSubscription && (
        <PaymentElement onReady={() => setLoading(false)} />
      )}

      {selectedTier.minTeamSize > 1 && (
        <div className="space-y-1.5">
          <Label htmlFor="team-size">Team Size</Label>
          <Input
            id="team-size"
            type="number"
            min={selectedTier.minTeamSize}
            value={teamSize}
            onChange={e => {
              const value =
                e.target.value === '' ? '' : parseInt(e.target.value)
              setTeamSize(value)
            }}
            onBlur={e => {
              const value =
                e.target.value === '' ? '' : parseInt(e.target.value)
              if (value !== '' && value < selectedTier.minTeamSize) {
                setTeamSize(selectedTier.minTeamSize)
              }
            }}
            aria-invalid={teamSizeInvalid}
          />
          <p
            className={cn(
              'text-xs',
              teamSizeInvalid ? 'text-destructive' : 'text-muted-foreground',
            )}
          >
            {teamSizeInvalid
              ? `Minimum team size is ${selectedTier.minTeamSize} users`
              : `Enter the number of users in your team (minimum ${selectedTier.minTeamSize})`}
          </p>
        </div>
      )}

      <div className="flex items-end gap-2">
        <div className="flex-1 space-y-1.5">
          <Label htmlFor="coupon-code">Coupon Code (Optional)</Label>
          <Input
            id="coupon-code"
            value={couponCode}
            onChange={handleCouponChange}
            disabled={applyingCoupon}
            aria-invalid={!!couponError}
          />
          <p
            className={cn(
              'text-xs',
              couponError ? 'text-destructive' : 'text-muted-foreground',
            )}
          >
            {couponError ||
              (hasAppliedCoupon &&
                couponDiscount?.percentOff &&
                `${couponDiscount.percentOff}% discount applied`) ||
              (hasAppliedCoupon &&
                couponDiscount?.amountOff &&
                `$${(couponDiscount.amountOff / 100).toFixed(2)} discount applied`) ||
              'Enter a valid coupon code'}
          </p>
        </div>
        <Button
          type="button"
          onClick={handleApplyCoupon}
          disabled={!canApplyCoupon || applyingCoupon || validatingCoupon}
        >
          {applyingCoupon ? 'Applying...' : 'Apply'}
        </Button>
        {hasAppliedCoupon && (
          <Button
            type="button"
            variant="outline"
            onClick={handleRemoveCoupon}
            disabled={applyingCoupon}
            className="border-destructive text-destructive hover:bg-destructive/10"
          >
            Remove
          </Button>
        )}
      </div>

      <Card className="gap-3 py-4">
        <CardContent className="px-4">
          <h6 className="text-base font-semibold mb-2">Cost Breakdown</h6>
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell>Tier</TableCell>
                  <TableCell className="text-right">
                    {selectedTier.name}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Base Price</TableCell>
                  <TableCell className="text-right">
                    ${selectedTier.price}/month
                    {selectedTier.team && ' per user'}
                  </TableCell>
                </TableRow>
                {selectedTier.team && (
                  <>
                    <TableRow>
                      <TableCell>Team Size</TableCell>
                      <TableCell className="text-right">
                        {teamSize || selectedTier.minTeamSize} users
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Base Team Cost</TableCell>
                      <TableCell className="text-right">
                        $
                        {(
                          selectedTier.price *
                          (teamSize || selectedTier.minTeamSize)
                        ).toFixed(2)}
                        /month
                      </TableCell>
                    </TableRow>
                  </>
                )}
                {hasAppliedCoupon && (
                  <TableRow>
                    <TableCell className="text-emerald-600">
                      Coupon Discount ({appliedCouponCode})
                    </TableCell>
                    <TableCell className="text-right text-emerald-600">
                      {couponDiscount.percentOff
                        ? `-${couponDiscount.percentOff}%`
                        : `-$${(couponDiscount.amountOff / 100).toFixed(2)}`}
                    </TableCell>
                  </TableRow>
                )}
                {existingBilling?.hasActiveSubscription && (
                  <>
                    <TableRow>
                      <TableCell>Current Monthly Cost</TableCell>
                      <TableCell className="text-right">
                        ${currentTierPrice.toFixed(2)}/month
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-bold">Price Change</TableCell>
                      <TableCell
                        className={cn(
                          'text-right font-bold',
                          calculatePriceDifference() >= 0
                            ? 'text-emerald-600'
                            : 'text-red-600',
                        )}
                      >
                        {calculatePriceDifference() >= 0 ? '+' : ''}$
                        {Math.abs(calculatePriceDifference()).toFixed(2)}
                        /month
                      </TableCell>
                    </TableRow>
                  </>
                )}
                <TableRow>
                  <TableCell className="font-bold">
                    {existingBilling?.hasActiveSubscription
                      ? 'New Monthly Cost'
                      : 'Total Monthly Cost'}
                  </TableCell>
                  <TableCell className="text-right font-bold">
                    ${calculateDiscountedPrice().toFixed(2)}/month
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button
          type="submit"
          disabled={submitting}
          className="bg-emerald-600 text-white hover:bg-emerald-700"
        >
          {submitting
            ? 'Processing...'
            : `${
                existingBilling?.hasActiveSubscription
                  ? 'Update Subscription'
                  : 'Subscribe'
              } for $${calculateDiscountedPrice().toFixed(2)} Now`}
        </Button>
        <Button type="button" variant="outline" onClick={handleBack}>
          Back
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={handleClose}
          className="border-destructive text-destructive hover:bg-destructive/10"
        >
          Cancel
        </Button>
      </div>
    </form>
  )
}
